import React, { useCallback, useEffect, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
} from "recharts";

function fmt(n) { return Number(n || 0).toFixed(2); }
function today() { return new Date().toISOString().slice(0, 10); }
function oneYearAgo() {
  const d = new Date(); d.setFullYear(d.getFullYear() - 1);
  return d.toISOString().slice(0, 10);
}

const SUB_TABS = [
  { id: "grafico",  label: "Gráfico evolución" },
  { id: "datos",    label: "Datos" },
  { id: "monedero", label: "Monedero Caja" },
];

export default function FlujoContable({ token, venueID }) {
  const [fromDate, setFromDate] = useState(oneYearAgo);
  const [toDate, setToDate]     = useState(today);
  const [subTab, setSubTab]     = useState("grafico");
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");

  const fetchData = useCallback(async () => {
    if (!token || !venueID) return;
    setLoading(true); setError("");
    try {
      const res = await fetch(
        `/v1/reports/cash-flow?venue_id=${venueID}&from_date=${fromDate}&to_date=${toDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Error al cargar flujo contable");
      setData(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, venueID, fromDate, toDate]);

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Thin chart series to ~every 7 days
  const chartSeries = (data?.cumulative_series || []).filter((_, i) => i % 7 === 0 || i === (data.cumulative_series.length - 1));
  const activeRows  = data?.active_table || [];
  const totalDeposits = activeRows.reduce((s, r) => s + r.depositos, 0);

  return (
    <div className="space-y-4">
      {/* Date range + balance banner */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-sm">
          <label className="text-gray-500 font-medium">Desde</label>
          <input
            type="date"
            className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-lime-400"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2 text-sm">
          <label className="text-gray-500 font-medium">hasta</label>
          <input
            type="date"
            className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-lime-400"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="px-4 py-1.5 bg-lime-500 hover:bg-lime-600 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
        >
          {loading ? "Cargando…" : "Ver Balance"}
        </button>
        {data && (
          <div className="ml-auto bg-gray-900 text-white text-sm font-bold px-4 py-2 rounded-lg">
            BALANCE TOTAL = {fmt(data.balance_total)} €
          </div>
        )}
      </div>

      {error && <div className="text-sm text-red-500 bg-red-50 rounded-lg px-3 py-2">{error}</div>}

      {/* Sub-tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {SUB_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              subTab === t.id ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && <div className="text-center py-16 text-gray-400 text-sm">Cargando datos…</div>}

      {!loading && data && subTab === "grafico" && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-400 mb-3">
            Flujo contable acumulado — {data.from_date} a {data.to_date}
          </p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartSeries} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="fecha" tick={{ fontSize: 9, fill: "#9ca3af" }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: "#9ca3af" }} />
                <ReferenceLine y={0} stroke="#e5e7eb" strokeDasharray="4 4" />
                <Tooltip
                  formatter={(v) => [`${fmt(v)} €`, "Balance acumulado"]}
                  contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e5e7eb" }}
                />
                <Line type="monotone" dataKey="balance" stroke="#84cc16" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {!loading && data && subTab === "datos" && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Fecha</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Total</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Ventas</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Depósitos</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Gastos</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {activeRows.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-10 text-gray-400 text-sm">Sin actividad en el período</td></tr>
              ) : (
                activeRows.map((row) => (
                  <tr key={row.fecha} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2.5 text-gray-700 font-medium">{row.fecha}</td>
                    <td className={`px-4 py-2.5 text-right font-semibold ${row.total < 0 ? "text-red-500" : "text-gray-900"}`}>
                      {fmt(row.total)} €
                    </td>
                    <td className="px-4 py-2.5 text-right text-lime-600">{row.ventas > 0 ? `${fmt(row.ventas)} €` : <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-2.5 text-right text-sky-600">{row.depositos > 0 ? `${fmt(row.depositos)} €` : <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-2.5 text-right text-violet-600">{row.pagos_terceros > 0 ? `${fmt(row.pagos_terceros)} €` : <span className="text-gray-300">—</span>}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && data && subTab === "monedero" && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex justify-between items-center">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Fecha</span>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Total depósitos — <span className="text-sky-600">{fmt(totalDeposits)} €</span>
            </span>
          </div>
          <div className="divide-y divide-gray-100">
            {activeRows.filter((r) => r.depositos > 0).length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-sm">Sin depósitos en el período</div>
            ) : (
              activeRows.filter((r) => r.depositos > 0).map((row) => (
                <div key={row.fecha} className="flex justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors text-sm">
                  <span className="text-gray-700 font-medium">{row.fecha}</span>
                  <span className="font-semibold text-sky-600">{fmt(row.depositos)} €</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
